/*
 * Ícones da interface.
 *
 * Substituem os emojis que vinham do projecto original. Um emoji é desenhado
 * pelo sistema operativo, por isso o mesmo ecrã tem três aspectos diferentes
 * num iPhone, num Android e num monitor da cozinha — e nenhum deles respeita
 * a paleta da casa. Estes são SVG traçados a `currentColor`: herdam a cor de
 * quem os usa e desenham-se sempre igual.
 *
 * Todos partilham a mesma grelha de 24 e a mesma espessura de traço, senão
 * lado a lado uns parecem mais pesados do que outros.
 */

import type { ReactElement } from 'react';

export interface IconProps {
  /** Lado do ícone em pixels. Por omissão acompanha o texto à volta. */
  tamanho?: number;
  className?: string;
}

/** Assinatura comum, para quem guarde ícones numa tabela de configuração. */
export type Icone = (props: IconProps) => ReactElement;

const base = (tamanho: number, className?: string) => ({
  width: tamanho,
  height: tamanho,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  focusable: false,
  className,
});

export const IconClose = ({ tamanho = 18, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export const IconPlus = ({ tamanho = 22, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const IconBell = ({ tamanho = 22, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9M10 21h4M12 2V1" />
  </svg>
);

export const IconLock = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}><rect x="5" y="10" width="14" height="11" rx="2" /><path d="M8 10V6a4 4 0 0 1 8 0v4M12 14v3" /></svg>
);

export const IconChevronLeft = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);

export const IconChevronRight = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

export const IconChevronDown = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const IconCheck = ({ tamanho = 24, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
  </svg>
);

export const IconCancel = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <circle cx="12" cy="12" r="9" />
    <path d="m9 9 6 6M15 9l-6 6" />
  </svg>
);

/** Carrinho de compras do menu do cliente. */
export const IconCart = ({ tamanho = 22, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M3 4h2.2l1.7 10.1a2 2 0 0 0 2 1.7h7.9a2 2 0 0 0 1.9-1.5L20.4 8H6" />
    <circle cx="9.3" cy="19.2" r="1.2" />
    <circle cx="17.3" cy="19.2" r="1.2" />
  </svg>
);

/** Pagamento ao balcão. */
export const IconCounter = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M3 9.5 4.6 5h14.8L21 9.5" />
    <path d="M3 9.5h18a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0Z" />
    <path d="M5 12v7h14v-7" />
  </svg>
);

/** Pagamento na aplicação. */
export const IconPhonePay = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <rect x="6" y="2.5" width="12" height="19" rx="2.6" />
    <path d="M10.6 18.4h2.8" />
    <path d="M9 8.4h6M9 11.6h3.5" />
  </svg>
);

/* --------------------------------------------- etapas do acompanhamento -- */

/** Pedido enviado, à espera do funcionário. */
export const IconReceipt = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M6 2.8h12v18.4l-2.4-1.6-2.4 1.6-2.4-1.6-2.4 1.6L6 21.2Z" />
    <path d="M9.4 8h5.2M9.4 12h5.2" />
  </svg>
);

/** Confirmado pelo funcionário. */
export const IconClipboard = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M9 4.2H7.4A1.4 1.4 0 0 0 6 5.6v14.2a1.4 1.4 0 0 0 1.4 1.4h9.2a1.4 1.4 0 0 0 1.4-1.4V5.6a1.4 1.4 0 0 0-1.4-1.4H15" />
    <rect x="9" y="2.5" width="6" height="3.6" rx="1.2" />
    <path d="M9.6 12.4h4.8M9.6 16h3" />
  </svg>
);

/** Em preparação na cozinha. */
export const IconCooking = ({ tamanho = 20, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M4.5 11h15v5.4a3 3 0 0 1-3 3h-9a3 3 0 0 1-3-3Z" />
    <path d="M3 11h18" />
    <path d="M9 7.4c0-1 .8-1.6.8-2.6 0-.7-.4-1.2-.8-1.5M14.6 7.4c0-1 .8-1.6.8-2.6 0-.7-.4-1.2-.8-1.5" />
  </svg>
);

/* ------------------------------------------------------------- dietas -- */

/** Vegan. */
export const IconLeaf = ({ tamanho = 16, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M20 4.5c0 8.4-4.3 12.6-9.6 12.6A4.9 4.9 0 0 1 5.5 12c0-5.3 5-7.5 14.5-7.5Z" />
    <path d="M4 20c2.6-4.2 6-7 10.4-9" />
  </svg>
);

/** Vegetariano. */
export const IconSprout = ({ tamanho = 16, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M12 21v-8" />
    <path d="M12 13C12 9.6 9.4 7 6 7c0 3.4 2.6 6 6 6Z" />
    <path d="M12 13c0-3.4 2.6-6 6-6 0 3.4-2.6 6-6 6Z" />
  </svg>
);

/** Sem glúten: a espiga com um traço por cima. */
export const IconWheatFree = ({ tamanho = 16, className }: IconProps) => (
  <svg {...base(tamanho, className)}>
    <path d="M12 20.5V10" />
    <path d="M12 10c-1.8-1-2.6-2.6-2.4-4.6 2 .2 3.2 1.4 3.6 3.2" />
    <path d="M12 14.4c-1.8-1-2.6-2.6-2.4-4.6 2 .2 3.2 1.4 3.6 3.2" />
    <path d="M12 10c1.8-1 2.6-2.6 2.4-4.6-2 .2-3.2 1.4-3.6 3.2" />
    <path d="M3.5 3.5l17 17" />
  </svg>
);

export const IconChart = ({ tamanho = 20, className }: IconProps) => <svg {...base(tamanho, className)}><path d="M4 4v16h16M8 15v-4M13 15V7M18 15v-6" /></svg>;
export const IconGrid = ({ tamanho = 20, className }: IconProps) => <svg {...base(tamanho, className)}><rect x="3" y="3" width="7" height="7" rx="2"/><rect x="14" y="3" width="7" height="7" rx="2"/><rect x="3" y="14" width="7" height="7" rx="2"/><rect x="14" y="14" width="7" height="7" rx="2"/></svg>;
export const IconTable = ({ tamanho = 20, className }: IconProps) => <svg {...base(tamanho, className)}><rect x="3" y="6" width="18" height="5" rx="2"/><path d="M6 11v9M18 11v9M6 16h12"/></svg>;
export const IconUsers = ({ tamanho = 20, className }: IconProps) => <svg {...base(tamanho, className)}><circle cx="9" cy="8" r="3"/><path d="M3 21v-2a6 6 0 0 1 12 0v2M17 5a3 3 0 0 1 0 6M18 15a5 5 0 0 1 3 4v2"/></svg>;
export const IconLogout = ({ tamanho = 20, className }: IconProps) => <svg {...base(tamanho, className)}><path d="M9 4H5v16h4M10 12h11m-4-4 4 4-4 4"/></svg>;
